import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Eye, EyeSlash, Copy, PencilSimple, Trash, LockKey, At, MagnifyingGlass, ShieldCheck, EnvelopeSimple, Timer, ClockCounterClockwise, Key, FolderSimple, User, Sparkle, CaretDown, WarningCircle, Plugs } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PasswordInput } from "../components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const emptyForm = { title: "", value: "", note: "", category: "", entry_type: "secret", email_address: "", username: "", email_group: "" };
const TYPE_META = {
  email: { label: "Email", icon: At, color: "text-sky-400", active: "bg-sky-400/15 text-sky-300 border-sky-400/40" },
  password: { label: "Password", icon: Key, color: "text-violet-400", active: "bg-violet-400/15 text-violet-300 border-violet-400/40" },
  secret: { label: "Codici", icon: LockKey, color: "text-amber-400", active: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  api: { label: "API", icon: Plugs, color: "text-cyan-400", active: "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" },
};

const generatePassword = () => {
  const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnopqrstuvwxyz", "23456789", "!@#$%&*+-_?"];
  const all = sets.join("");
  const rand = (n) => {
    const a = new Uint32Array(1);
    window.crypto.getRandomValues(a);
    return a[0] % n;
  };
  const chars = sets.map((s) => s[rand(s.length)]);
  while (chars.length < 16) chars.push(all[rand(all.length)]);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};
const REVEAL_HIDE_MS = 30000;

export const CATEGORIES = [
  { key: "", label: "Nessuna", dot: "bg-white/20", badge: "bg-white/5 text-white/50 border-white/15", accent: "" },
  { key: "rosso", label: "Rosso", dot: "bg-red-500", badge: "bg-red-500/15 text-red-400 border-red-500/30", accent: "border-l-red-500" },
  { key: "ambra", label: "Ambra", dot: "bg-amber-400", badge: "bg-amber-400/15 text-amber-300 border-amber-400/30", accent: "border-l-amber-400" },
  { key: "verde", label: "Verde", dot: "bg-emerald-400", badge: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30", accent: "border-l-emerald-400" },
  { key: "blu", label: "Blu", dot: "bg-sky-400", badge: "bg-sky-400/15 text-sky-300 border-sky-400/30", accent: "border-l-sky-400" },
  { key: "viola", label: "Viola", dot: "bg-violet-400", badge: "bg-violet-400/15 text-violet-300 border-violet-400/30", accent: "border-l-violet-400" },
  { key: "rosa", label: "Rosa", dot: "bg-pink-400", badge: "bg-pink-400/15 text-pink-300 border-pink-400/30", accent: "border-l-pink-400" },
];
const catOf = (key) => CATEGORIES.find((c) => c.key === key) || CATEGORIES[0];
const inputCls = "h-11 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40";

const readStoredToken = () => {
  const token = sessionStorage.getItem("vault_token");
  const exp = parseInt(sessionStorage.getItem("vault_token_exp") || "0", 10);
  if (token && exp > Date.now() + 2000) return { token, exp };
  return null;
};

const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;
const passwordAgeMonths = (e) => {
  if (!e.value_updated_at) return null;
  const age = Date.now() - new Date(e.value_updated_at).getTime();
  return age > SIX_MONTHS_MS ? Math.floor(age / (30.44 * 24 * 60 * 60 * 1000)) : null;
};

const tokenIsExtended = (token) => {
  try {
    return !!JSON.parse(atob(token.split(".")[1])).extended;
  } catch {
    return false;
  }
};

export default function LinkPage() {
  const stored = readStoredToken();
  const [vaultToken, setVaultToken] = useState(stored?.token || null);
  const [tokenExp, setTokenExp] = useState(stored?.exp || 0);
  const [remaining, setRemaining] = useState(0);
  const [extendUsed, setExtendUsed] = useState(stored ? tokenIsExtended(stored.token) : false);
  const [extending, setExtending] = useState(false);
  const [otpStep, setOtpStep] = useState("idle"); // idle | sent
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [unlockMinutes, setUnlockMinutes] = useState(10);

  useEffect(() => {
    api.get("/admin/vault/unlock-duration").then(({ data }) => setUnlockMinutes(data.minutes)).catch(() => {});
  }, []);

  const changeDuration = async (m) => {
    const prev = unlockMinutes;
    setUnlockMinutes(m);
    try {
      const { data } = await api.put("/admin/vault/unlock-duration", { minutes: m });
      toast.success(data.message);
    } catch (err) {
      setUnlockMinutes(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [entries, setEntries] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [revealed, setRevealed] = useState({});
  const hideTimers = useRef({});
  const [filterCat, setFilterCat] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroup = (name) => setCollapsedGroups((c) => ({ ...c, [name]: !c[name] }));

  const loadGroups = () => api.get("/admin/vault/email-groups", { headers: { "X-Vault-Token": readStoredToken()?.token || vaultToken } })
    .then(({ data }) => setGroups(data.groups)).catch(() => {});

  const createGroup = async () => {
    const name = window.prompt("Nome del nuovo gruppo:");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/admin/vault/email-groups", { name: name.trim() }, { headers: vaultHeaders });
      setGroups(data.groups);
      setForm((f) => ({ ...f, email_group: name.trim() }));
      toast.success(`Gruppo "${name.trim()}" creato`);
    } catch (err) {
      if (!handleVaultError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteGroup = async (name) => {
    try {
      const { data } = await api.delete(`/admin/vault/email-groups/${encodeURIComponent(name)}`, { headers: vaultHeaders });
      setGroups(data.groups);
      toast.success(data.message);
      load();
    } catch (err) {
      if (!handleVaultError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const vaultHeaders = { "X-Vault-Token": vaultToken };

  const extendSession = async () => {
    setExtending(true);
    try {
      const { data } = await api.post("/admin/vault/extend", {}, { headers: vaultHeaders });
      const exp = Date.now() + data.expires_in * 1000;
      sessionStorage.setItem("vault_token", data.vault_token);
      sessionStorage.setItem("vault_token_exp", String(exp));
      setVaultToken(data.vault_token);
      setTokenExp(exp);
      setExtendUsed(true);
      toast.success(data.message);
    } catch (err) {
      if (!handleVaultError(err)) {
        setExtendUsed(true);
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setExtending(false);
    }
  };

  const lock = (silent = false) => {
    sessionStorage.removeItem("vault_token");
    sessionStorage.removeItem("vault_token_exp");
    setVaultToken(null);
    setTokenExp(0);
    setExtendUsed(false);
    setEntries([]);
    setAccessLog([]);
    setRevealed({});
    setOtpStep("idle");
    setOtpCode("");
    if (!silent) toast.info("Sessione Cassaforte scaduta: richiedi un nuovo codice");
  };

  const handleVaultError = (err) => {
    if (err.response?.status === 403 && err.response?.data?.detail === "VAULT_LOCKED") {
      lock();
      return true;
    }
    return false;
  };

  const load = () => {
    api.get("/admin/vault", { headers: vaultHeaders })
      .then(({ data }) => setEntries(data))
      .catch((err) => { if (!handleVaultError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail)); });
    api.get("/admin/vault/access-log", { headers: vaultHeaders })
      .then(({ data }) => setAccessLog(data)).catch(() => {});
  };

  useEffect(() => { if (vaultToken) { load(); loadGroups(); } }, [vaultToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!vaultToken) return;
    const id = setInterval(() => {
      const left = Math.max(0, Math.round((tokenExp - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) lock();
    }, 1000);
    return () => clearInterval(id);
  }, [vaultToken, tokenExp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => Object.values(hideTimers.current).forEach(clearTimeout), []);

  const requestOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await api.post("/admin/vault/otp/request");
      toast.success(data.message);
      setOtpStep("sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      if (err.response?.status === 429 && (err.response?.data?.detail || "").includes("già inviato")) setOtpStep("sent");
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setOtpBusy(true);
    try {
      const { data } = await api.post("/admin/vault/otp/verify", { code: otpCode.trim() });
      const exp = Date.now() + data.expires_in * 1000;
      sessionStorage.setItem("vault_token", data.vault_token);
      sessionStorage.setItem("vault_token_exp", String(exp));
      setVaultToken(data.vault_token);
      setTokenExp(exp);
      setExtendUsed(false);
      setRemaining(data.expires_in);
      setOtpCode("");
      setOtpStep("idle");
      toast.success(`Cassaforte sbloccata per ${Math.round(data.expires_in / 60)} minuti`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOtpBusy(false);
    }
  };

  const hideValue = (id) => {
    setRevealed((r) => { const c = { ...r }; delete c[id]; return c; });
    clearTimeout(hideTimers.current[id]);
    delete hideTimers.current[id];
  };

  const toggleReveal = async (entry) => {
    if (revealed[entry.id] !== undefined) { hideValue(entry.id); return; }
    try {
      const { data } = await api.get(`/admin/vault/${entry.id}/reveal`, { headers: vaultHeaders });
      setRevealed((r) => ({ ...r, [entry.id]: data.value }));
      hideTimers.current[entry.id] = setTimeout(() => hideValue(entry.id), REVEAL_HIDE_MS);
    } catch (err) {
      if (!handleVaultError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyText = async (text, msg = "Copiato negli appunti") => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch (err) { toast.error("Copia non riuscita"); }
  };

  const copyValue = async (entry) => {
    try {
      let value = revealed[entry.id];
      if (value === undefined) {
        const { data } = await api.get(`/admin/vault/${entry.id}/reveal`, { headers: vaultHeaders });
        value = data.value;
      }
      await copyText(value, entry.entry_type === "email" || entry.entry_type === "password" ? "Password copiata" : "Copiato negli appunti");
    } catch (err) {
      if (!handleVaultError(err)) toast.error("Copia non riuscita");
    }
  };

  const openAdd = (type = "secret") => { setEditing(null); setForm({ ...emptyForm, entry_type: type }); setDialogOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ title: e.title, value: "", note: e.note || "", category: e.category || "", entry_type: e.entry_type || "secret", email_address: e.email_address || "", username: e.username || "", email_group: e.email_group || "" });
    setDialogOpen(true);
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { title: form.title, note: form.note, category: form.category, entry_type: form.entry_type, email_address: form.email_address, username: form.username, email_group: form.email_group };
        if (form.value) payload.value = form.value;
        await api.put(`/admin/vault/${editing.id}`, payload, { headers: vaultHeaders });
        hideValue(editing.id);
        toast.success("Voce aggiornata");
      } else {
        await api.post("/admin/vault", form, { headers: vaultHeaders });
        toast.success(form.entry_type === "email" ? "Email salvata (password cifrata)" : form.entry_type === "password" ? "Password salvata (cifrata)" : "Voce salvata (cifrata)");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      if (!handleVaultError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/vault/${deleting.id}`, { headers: vaultHeaders });
      toast.success("Voce eliminata");
      setDeleting(null);
      load();
    } catch (err) {
      if (!handleVaultError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---------- Schermata di blocco ----------
  if (!vaultToken) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="link-page">
        <div className="mb-10 animate-fade-up">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Area riservata</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Cassaforte</h1>
        </div>
        <div className="max-w-md mx-auto p-10 rounded-lg bg-surface border border-surface-border text-center animate-fade-up" data-testid="vault-lock-screen">
          <ShieldCheck size={48} weight="duotone" className="text-amber-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-2">Verifica a due passaggi</h2>
          {otpStep === "idle" ? (
            <>
              <p className="text-sm text-slate-400 mb-5">
                Per aprire la Cassaforte ti inviamo un codice a 6 cifre alla tua email. Il codice vale 5 minuti; la Cassaforte resta aperta {unlockMinutes} minuti, poi si blocca da sola.
              </p>
              <div className="mb-6" data-testid="vault-duration-picker">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2">Durata sblocco</p>
                <div className="inline-flex gap-2">
                  {[5, 10, 30].map((m) => (
                    <button key={m} type="button" data-testid={`vault-duration-${m}`} onClick={() => changeDuration(m)}
                      className={`h-8 px-4 rounded-full text-xs font-semibold border transition-colors ${unlockMinutes === m ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                      {m} min
                    </button>
                  ))}
                </div>
              </div>
              <Button data-testid="vault-otp-request-button" onClick={requestOtp} disabled={otpBusy}
                className="h-11 px-8 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
                <EnvelopeSimple size={16} className="mr-2" /> {otpBusy ? "Invio..." : "Inviami il codice"}
              </Button>
            </>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4">
              <p className="text-sm text-slate-400">Inserisci il codice a 6 cifre che hai ricevuto via email.</p>
              <Input required autoFocus data-testid="vault-otp-code-input" value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="• • • • • •" maxLength={6}
                className="h-14 bg-transparent border-white/15 text-white text-center text-2xl font-mono tracking-[0.5em] placeholder:text-white/20" />
              <Button type="submit" data-testid="vault-otp-verify-button" disabled={otpBusy || otpCode.length !== 6}
                className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
                {otpBusy ? "Verifica..." : "Sblocca la Cassaforte"}
              </Button>
              <button type="button" data-testid="vault-otp-resend-button" onClick={requestOtp} disabled={otpBusy}
                className="text-xs text-white/40 hover:text-white transition-colors">
                Non è arrivato? Invia di nuovo (attendi 1 minuto)
              </button>
            </form>
          )}
          <p className="text-[11px] text-white/30 mt-6">
            Dopo 5 codici errati la Cassaforte si blocca per 15 minuti. Ogni apertura viene registrata e ti viene notificata via email.
          </p>
        </div>
      </main>
    );
  }

  // ---------- Cassaforte sbloccata ----------
  const typeOf = (e) => e.entry_type || "secret";
  const emailCount = entries.filter((e) => typeOf(e) === "email").length;
  const passwordCount = entries.filter((e) => typeOf(e) === "password").length;
  const apiCount = entries.filter((e) => typeOf(e) === "api").length;
  const secretCount = entries.length - emailCount - passwordCount - apiCount;
  const q = search.trim().toLowerCase();
  const filteredEntries = entries
    .filter((e) => filterCat === null || (e.category || "") === filterCat)
    .filter((e) => filterType === null || typeOf(e) === filterType)
    .filter((e) => !q || e.title.toLowerCase().includes(q) || (e.email_address || "").toLowerCase().includes(q) || (e.note || "").toLowerCase().includes(q) || (e.email_group || "").toLowerCase().includes(q));
  const isEmailForm = form.entry_type === "email";
  const isPasswordForm = form.entry_type === "password";
  const emailView = filterType === "email";
  const groupedEmails = emailView
    ? [...groups.filter((g) => true).map((g) => ({ name: g, items: filteredEntries.filter((e) => (e.email_group || "") === g) })),
       { name: "", items: filteredEntries.filter((e) => !e.email_group || !groups.includes(e.email_group)) }]
    : null;
  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="link-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4 animate-fade-up">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Area riservata</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Cassaforte</h1>
            <p className="text-sm text-slate-400 mt-3">Codici segreti, email con password e chiavi API dei servizi esterni, cifrati nel database. I valori rivelati si nascondono da soli dopo 30 secondi.</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="vault-session-countdown" title="Tempo rimanente prima del blocco automatico"
              className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full border text-xs font-semibold ${remaining <= 60 ? "border-red-500/40 text-red-400 bg-red-500/10" : "border-emerald-400/30 text-emerald-300 bg-emerald-400/5"}`}>
              <Timer size={14} weight="duotone" /> {mm}:{ss}
            </span>
            {!extendUsed && (
              <button type="button" data-testid="vault-extend-button" onClick={extendSession} disabled={extending}
                title="Prolunga la sessione di 10 minuti (una sola volta)"
                className="h-9 px-4 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                <Plus size={13} weight="bold" /> {extending ? "..." : "Prolunga 10 min"}
              </button>
            )}
            <Button data-testid="add-vault-button" onClick={() => openAdd(filterType || "secret")} className="bg-white text-black hover:bg-gray-200 rounded-full h-11 px-6 font-semibold text-sm">
              <Plus size={18} className="mr-1" /> Nuova voce
            </Button>
          </div>
        </div>

        {entries.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-8 animate-fade-up" data-testid="vault-filters">
            <button type="button" data-testid="vault-filter-all" onClick={() => { setFilterType(null); setFilterCat(null); }}
              className={`h-8 px-4 rounded-full text-xs font-semibold border transition-colors ${filterType === null && filterCat === null ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:text-white hover:bg-white/5"}`}>
              Tutte ({entries.length})
            </button>
            {emailCount > 0 && (
              <button type="button" data-testid="vault-filter-email" onClick={() => setFilterType(filterType === "email" ? null : "email")}
                className={`h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-semibold border transition-colors ${filterType === "email" ? "bg-sky-400/15 text-sky-300 border-sky-400/40 ring-1 ring-white/40" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                <At size={13} weight="duotone" /> Email ({emailCount})
              </button>
            )}
            {passwordCount > 0 && (
              <button type="button" data-testid="vault-filter-password" onClick={() => setFilterType(filterType === "password" ? null : "password")}
                className={`h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-semibold border transition-colors ${filterType === "password" ? "bg-violet-400/15 text-violet-300 border-violet-400/40 ring-1 ring-white/40" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                <Key size={13} weight="duotone" /> Password ({passwordCount})
              </button>
            )}
            {secretCount > 0 && (
              <button type="button" data-testid="vault-filter-secret" onClick={() => setFilterType(filterType === "secret" ? null : "secret")}
                className={`h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-semibold border transition-colors ${filterType === "secret" ? "bg-amber-400/15 text-amber-300 border-amber-400/40 ring-1 ring-white/40" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                <LockKey size={13} weight="duotone" /> Codici ({secretCount})
              </button>
            )}
            {apiCount > 0 && (
              <button type="button" data-testid="vault-filter-api" onClick={() => setFilterType(filterType === "api" ? null : "api")}
                className={`h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-semibold border transition-colors ${filterType === "api" ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40 ring-1 ring-white/40" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                <Plugs size={13} weight="duotone" /> API ({apiCount})
              </button>
            )}
            <span className="w-px h-5 bg-white/10 mx-1" />
            {CATEGORIES.filter((c) => c.key && entries.some((e) => e.category === c.key)).map((c) => {
              const count = entries.filter((e) => e.category === c.key).length;
              return (
                <button key={c.key} type="button" data-testid={`vault-filter-${c.key}`} title={`${c.label} (${count})`}
                  onClick={() => setFilterCat(filterCat === c.key ? null : c.key)}
                  className={`h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs font-semibold border transition-all ${filterCat === c.key ? `${c.badge} ring-1 ring-white/60` : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} /> {count}
                </button>
              );
            })}
            <div className="relative ml-auto">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input data-testid="vault-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca titolo, email o gruppo..."
                className="h-8 w-48 pl-8 bg-transparent border-white/10 text-white text-xs placeholder:text-white/25 rounded-full" />
            </div>
          </div>
        )}

        <div className="space-y-3 mt-5">
          {entries.length === 0 && (
            <div className="border border-surface-border rounded-lg bg-surface p-10 text-center animate-fade-up" data-testid="vault-empty">
              <LockKey size={40} weight="duotone" className="text-white/30 mx-auto mb-3" />
              <p className="text-sm text-slate-400">Nessuna voce salvata. Clicca "Nuova voce" per iniziare.</p>
            </div>
          )}
          {entries.length > 0 && filteredEntries.length === 0 && (
            <div className="border border-surface-border rounded-lg bg-surface p-10 text-center animate-fade-up" data-testid="vault-filter-empty">
              <p className="text-sm text-slate-400">Nessuna voce trovata con questi filtri.</p>
            </div>
          )}
          {(() => {
            const renderEntry = (e, i) => {
            const t = typeOf(e);
            const isEmail = t === "email";
            const isPwd = t === "password";
            const oldMonths = (isEmail || isPwd || t === "api") ? passwordAgeMonths(e) : null;
            const TIcon = TYPE_META[t]?.icon || LockKey;
            return (
              <div key={e.id} data-testid={`vault-entry-${e.title}`} className={`border border-surface-border rounded-lg bg-surface p-5 animate-fade-up ${e.category ? `border-l-4 ${catOf(e.category).accent}` : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold tracking-tight text-lg flex items-center gap-2">
                      <TIcon size={16} weight="duotone" className={`${TYPE_META[t]?.color || "text-amber-400"} shrink-0`} />
                      {e.title}
                      {isEmail && e.email_group && !emailView && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2.5 py-0.5 bg-sky-400/10 text-sky-300 border-sky-400/25">
                          <FolderSimple size={11} /> {e.email_group}
                        </span>
                      )}
                      {oldMonths !== null && (
                        <span data-testid={`vault-old-password-${e.title}`}
                          title={t === "api"
                            ? `La chiave API non viene aggiornata da circa ${oldMonths} mesi: verifica che sia ancora valida o ruotala`
                            : `La password non viene cambiata da circa ${oldMonths} mesi: valuta di aggiornarla`}
                          className="inline-flex items-center gap-1 text-[10px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2.5 py-0.5 bg-red-500/10 text-red-400 border-red-500/30">
                          <WarningCircle size={11} weight="fill" /> {t === "api" ? "Chiave da verificare" : "Password da cambiare"}
                        </span>
                      )}
                      {e.category && (
                        <span data-testid={`vault-category-${e.title}`} className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2.5 py-0.5 ${catOf(e.category).badge}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${catOf(e.category).dot}`} /> {catOf(e.category).label}
                        </span>
                      )}
                    </h3>
                    {isEmail && e.email_address && (
                      <button type="button" data-testid={`vault-email-${e.title}`} onClick={() => copyText(e.email_address, "Indirizzo email copiato")}
                        title="Clicca per copiare l'indirizzo"
                        className="text-sm font-mono text-sky-300 hover:text-sky-200 mt-0.5 inline-flex items-center gap-1.5 transition-colors">
                        {e.email_address} <Copy size={12} className="opacity-50" />
                      </button>
                    )}
                    {isPwd && e.username && (
                      <button type="button" data-testid={`vault-username-${e.title}`} onClick={() => copyText(e.username, "Username copiato")}
                        title="Clicca per copiare l'username"
                        className="text-sm font-mono text-violet-300 hover:text-violet-200 mt-0.5 inline-flex items-center gap-1.5 transition-colors">
                        <User size={12} className="opacity-60" /> {e.username} <Copy size={12} className="opacity-50" />
                      </button>
                    )}
                    {e.note && <p className="text-xs text-slate-400 mt-0.5">{e.note}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" data-testid={`reveal-${e.title}`} onClick={() => toggleReveal(e)} className="text-white/60 hover:text-white hover:bg-white/5 h-8 w-8 p-0 rounded-full" title={revealed[e.id] !== undefined ? "Nascondi" : isEmail || isPwd ? "Mostra password" : "Mostra"}>
                      {revealed[e.id] !== undefined ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`copy-${e.title}`} onClick={() => copyValue(e)} className="text-white/60 hover:text-white hover:bg-white/5 h-8 w-8 p-0 rounded-full" title={isEmail || isPwd ? "Copia password" : "Copia"}>
                      <Copy size={16} />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`edit-vault-${e.title}`} onClick={() => openEdit(e)} className="text-white/60 hover:text-white hover:bg-white/5 h-8 w-8 p-0 rounded-full" title="Modifica">
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`delete-vault-${e.title}`} onClick={() => setDeleting(e)} className="text-red-500/60 hover:text-red-500 hover:bg-red-500/10 h-8 w-8 p-0 rounded-full" title="Elimina">
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
                {revealed[e.id] !== undefined && (
                  <div className="mt-3">
                    <pre data-testid={`vault-value-${e.title}`} className="text-sm font-mono text-emerald-300 bg-black/40 border border-white/10 rounded-md px-4 py-3 whitespace-pre-wrap break-all">{isEmail || isPwd ? `Password: ${revealed[e.id]}` : revealed[e.id]}</pre>
                    <p className="text-[10px] text-white/30 mt-1">Si nasconde automaticamente tra 30 secondi.</p>
                  </div>
                )}
              </div>
            );
            };
            if (!emailView) return filteredEntries.map(renderEntry);
            return (
              <div className="space-y-6" data-testid="vault-email-groups-view">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-white/40">Email organizzate per gruppo.</p>
                  <button type="button" data-testid="vault-new-group-button" onClick={createGroup}
                    className="h-8 px-4 rounded-full text-xs font-semibold border border-sky-400/40 text-sky-300 hover:bg-sky-400/10 transition-colors inline-flex items-center gap-1.5">
                    <Plus size={13} /> Nuovo gruppo
                  </button>
                </div>
                {groupedEmails.map((g) => {
                  const gKey = g.name || "__none";
                  const collapsed = !!collapsedGroups[gKey];
                  return (g.name || g.items.length > 0) && (
                    <div key={gKey} data-testid={`vault-group-${g.name || "senza-gruppo"}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <button type="button" data-testid={`vault-group-toggle-${g.name || "senza-gruppo"}`}
                          onClick={() => toggleGroup(gKey)}
                          title={collapsed ? "Apri gruppo" : "Chiudi gruppo"}
                          className="text-xs tracking-[0.2em] uppercase text-sky-300/80 hover:text-sky-200 flex items-center gap-1.5 transition-colors">
                          <CaretDown size={13} weight="bold" className={`transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`} />
                          <FolderSimple size={13} weight="duotone" /> {g.name || "Senza gruppo"} ({g.items.length})
                        </button>
                        {g.name && (
                          <button type="button" data-testid={`vault-group-delete-${g.name}`} onClick={() => deleteGroup(g.name)}
                            title="Elimina gruppo (le email restano)"
                            className="text-white/25 hover:text-red-400 transition-colors">
                            <Trash size={13} />
                          </button>
                        )}
                      </div>
                      {!collapsed && (
                        <div className="space-y-3">
                          {g.items.length === 0
                            ? <p className="text-xs text-white/30 border border-dashed border-white/10 rounded-lg px-4 py-3">Nessuna email in questo gruppo.</p>
                            : g.items.map(renderEntry)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {accessLog.length > 0 && (
          <div className="mt-12 animate-fade-up" data-testid="vault-access-log">
            <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-1.5">
              <ClockCounterClockwise size={13} className="text-amber-400" /> Registro accessi Cassaforte
            </p>
            <div className="border border-surface-border rounded-lg bg-surface overflow-hidden overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left border-b border-white/5">
                    <th className="py-2.5 px-4 font-medium">Utente</th>
                    <th className="py-2.5 px-4 font-medium">Data e ora</th>
                    <th className="py-2.5 px-4 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {accessLog.slice(0, 10).map((r, i) => (
                    <tr key={`${r.timestamp}-${i}`} className="border-t border-white/5 text-white/70">
                      <td className="py-2 px-4">{r.email}</td>
                      <td className="py-2 px-4">{new Date(r.timestamp).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
                      <td className="py-2 px-4 font-mono text-white/45">{r.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-md" data-testid="vault-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">{editing ? "Modifica voce" : "Nuova voce"}</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {isEmailForm || isPasswordForm ? "La password viene cifrata prima di essere salvata nel database." : "Il valore viene cifrato prima di essere salvato nel database."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Tipo</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="vault-type-picker">
                {["email", "password", "secret", "api"].map((t) => {
                  const m = TYPE_META[t];
                  const TIcon = m.icon;
                  return (
                    <button key={t} type="button" data-testid={`vault-type-${t}`}
                      onClick={() => setForm({ ...form, entry_type: t })}
                      className={`h-10 rounded-lg text-xs font-semibold border inline-flex items-center justify-center gap-1.5 transition-colors ${form.entry_type === t ? m.active : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                      <TIcon size={14} weight="duotone" /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Titolo</Label>
              <Input required data-testid="vault-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={isEmailForm ? "Es. PEC amministrazione" : isPasswordForm ? "Es. Account Aruba" : form.entry_type === "api" ? "Es. Tavily — Ricerca web" : "Es. Segreti .env AREAPAQ"} className={inputCls} />
            </div>
            {isEmailForm ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Indirizzo email</Label>
                  <Input required type="email" data-testid="vault-email-input" value={form.email_address}
                    onChange={(e) => setForm({ ...form, email_address: e.target.value })}
                    placeholder="nome@azienda.it" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Gruppo</Label>
                  <div className="flex flex-wrap items-center gap-2" data-testid="vault-group-picker">
                    <button type="button" data-testid="vault-group-option-none" onClick={() => setForm({ ...form, email_group: "" })}
                      className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${!form.email_group ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                      Nessuno
                    </button>
                    {groups.map((g) => (
                      <button key={g} type="button" data-testid={`vault-group-option-${g}`} onClick={() => setForm({ ...form, email_group: g })}
                        className={`h-8 px-3 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors ${form.email_group === g ? "bg-sky-400/15 text-sky-300 border-sky-400/40 ring-1 ring-white/40" : "border-white/15 text-white/50 hover:text-white hover:bg-white/5"}`}>
                        <FolderSimple size={12} /> {g}
                      </button>
                    ))}
                    <button type="button" data-testid="vault-group-create-inline" onClick={createGroup}
                      className="h-8 px-3 rounded-full text-xs font-semibold border border-dashed border-sky-400/40 text-sky-300 hover:bg-sky-400/10 transition-colors inline-flex items-center gap-1">
                      <Plus size={12} /> Nuovo gruppo
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs tracking-[0.2em] uppercase text-white/60">
                      Password {editing && <span className="text-white/30 normal-case tracking-normal">(lascia vuoto per non cambiare)</span>}
                    </Label>
                    <button type="button" data-testid="vault-generate-password" onClick={() => { const p = generatePassword(); setForm((f) => ({ ...f, value: p })); (navigator.clipboard?.writeText(p) || Promise.reject()).then(() => toast.success("Password forte generata e copiata")).catch(() => toast.success("Password forte generata")); }}
                      className="h-7 px-3 rounded-full text-[11px] font-semibold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors inline-flex items-center gap-1">
                      <Sparkle size={12} weight="fill" /> Genera forte
                    </button>
                  </div>
                  <PasswordInput required={!editing} data-testid="vault-password-input" value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    placeholder="Password della casella" className={inputCls} />
                </div>
              </>
            ) : isPasswordForm ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Username (facoltativo)</Label>
                  <Input data-testid="vault-username-input" value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="Es. nome utente o email di accesso" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs tracking-[0.2em] uppercase text-white/60">
                      Password {editing && <span className="text-white/30 normal-case tracking-normal">(lascia vuoto per non cambiare)</span>}
                    </Label>
                    <button type="button" data-testid="vault-generate-password" onClick={() => { const p = generatePassword(); setForm((f) => ({ ...f, value: p })); (navigator.clipboard?.writeText(p) || Promise.reject()).then(() => toast.success("Password forte generata e copiata")).catch(() => toast.success("Password forte generata")); }}
                      className="h-7 px-3 rounded-full text-[11px] font-semibold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors inline-flex items-center gap-1">
                      <Sparkle size={12} weight="fill" /> Genera forte
                    </button>
                  </div>
                  <PasswordInput required={!editing} data-testid="vault-pwd-value-input" value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    placeholder="Password del servizio" className={inputCls} />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">
                  {form.entry_type === "api" ? "Chiave API" : "Valore segreto"} {editing && <span className="text-white/30 normal-case tracking-normal">(lascia vuoto per non cambiare)</span>}
                </Label>
                <Textarea required={!editing} data-testid="vault-value-input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} rows={4} placeholder={form.entry_type === "api" ? "Incolla qui la chiave API..." : "Incolla qui il codice segreto..."} className="bg-transparent border-white/10 text-white font-mono text-sm placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Etichetta colore</Label>
              <div className="flex flex-wrap items-center gap-2" data-testid="vault-category-picker">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key || "none"}
                    type="button"
                    data-testid={`vault-category-option-${c.key || "none"}`}
                    title={c.label}
                    onClick={() => setForm({ ...form, category: c.key })}
                    className={`w-7 h-7 rounded-full ${c.dot} border transition-transform ${form.category === c.key ? "ring-2 ring-white ring-offset-2 ring-offset-[#12121A] border-transparent scale-110" : "border-white/20 hover:scale-110"}`}
                  />
                ))}
                <span className="text-xs text-white/50 ml-1">{catOf(form.category).label}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nota (facoltativa)</Label>
              <Input data-testid="vault-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder={isEmailForm ? "Es. casella usata per le fatture" : "A cosa serve questo codice"} className={inputCls} />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" disabled={saving} data-testid="vault-submit-button" className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
                {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Salva (cifrato)"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent className="bg-surface border-white/10 text-white" data-testid="vault-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Eliminare la voce?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Stai per eliminare <span className="text-white">{deleting?.title}</span>. Il valore cifrato andrà perso definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="vault-delete-confirm" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-full">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
